import React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  total?: React.ReactNode;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export default function Pagination({
  page,
  totalPages,
  onChange,
  total,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  disabled = false,
  style,
  className,
}: Props) {
  const tp = Math.max(1, totalPages);
  const currentPage = Math.min(Math.max(1, page), tp);
  // table 布局下禁止 borderTop，在组件层面统一过滤掉
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { borderTop: _borderTop, ...safeStyle } = (style ?? {}) as React.CSSProperties & { borderTop?: string };

  let pages: (number | -1)[];
  if (tp <= 7) {
    pages = Array.from({ length: tp }, (_, i) => i + 1);
  } else if (currentPage <= 4) {
    pages = [1, 2, 3, 4, 5, -1, tp];
  } else if (currentPage >= tp - 3) {
    pages = [1, -1, tp - 4, tp - 3, tp - 2, tp - 1, tp];
  } else {
    pages = [1, -1, currentPage - 1, currentPage, currentPage + 1, -1, tp];
  }

  const go = (nextPage: number) => {
    if (!disabled && nextPage >= 1 && nextPage <= tp && nextPage !== currentPage) onChange(nextPage);
  };

  const sizeOptions = pageSize == null
    ? []
    : Array.from(new Set([...pageSizeOptions, pageSize])).sort((left, right) => left - right);

  return (
    <div className={`pagination-bar${className ? ` ${className}` : ''}`} style={safeStyle}>
      {total != null && <span className="pg-total">{total}</span>}
      <div className="pg-controls">
        <div className="pg-btns">
          <button type="button" className="pg-btn pg-nav" aria-label="上一页" disabled={disabled || currentPage <= 1} onClick={() => go(currentPage - 1)}><ChevronLeft size={18} /></button>
          {pages.map((pageNumber, index) =>
            pageNumber === -1
              ? <span key={`e${index}`} className="pg-ellipsis">…</span>
              : <button type="button" key={pageNumber} className={`pg-btn${pageNumber === currentPage ? ' active' : ''}`} aria-current={pageNumber === currentPage ? 'page' : undefined} disabled={disabled} onClick={() => go(pageNumber)}>{pageNumber}</button>
          )}
          <button type="button" className="pg-btn pg-nav" aria-label="下一页" disabled={disabled || currentPage >= tp} onClick={() => go(currentPage + 1)}><ChevronRight size={18} /></button>
        </div>
        {pageSize != null && (
          <label className="pg-size">
            <select
              aria-label="每页条数"
              value={pageSize}
              disabled={disabled || !onPageSizeChange}
              onChange={event => onPageSizeChange?.(Number(event.target.value))}
            >
              {sizeOptions.map(option => <option key={option} value={option}>{option} 条/页</option>)}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </label>
        )}
      </div>
    </div>
  );
}
